<script setup lang="ts">
const _props = defineProps<{
  data: MediaCarouselProps
  template?: 'page' | 'overlay'
}>()

const $carousel = useTemplateRef<HTMLElement>('$carousel')

function nextSlide() {
  const el = $carousel.value
  if (!el) return

  const item = el.querySelector('li')
  if (!item) return

  const scrollAmount = item.clientWidth + parseFloat(getComputedStyle(item).marginRight || '0')

  el.scrollBy({ left: scrollAmount, behavior: 'smooth' })
}

function previousSlide() {
  const el = $carousel.value
  if (!el) return

  const item = el.querySelector('li')
  if (!item) return

  const scrollAmount = item.clientWidth + parseFloat(getComputedStyle(item).marginRight || '0')

  el.scrollBy({ left: -scrollAmount, behavior: 'smooth' })
}

const canScrollLeft = ref(false)
const canScrollRight = ref(false)

watchEffect(() => {
  const el = $carousel.value
  if (!el) return

  const updateScrollStatus = () => {
    canScrollLeft.value = el.scrollLeft > 0
    canScrollRight.value = el.scrollLeft + el.clientWidth < el.scrollWidth
  }

  updateScrollStatus()
  el.addEventListener('scroll', updateScrollStatus)

  onUnmounted(() => {
    el.removeEventListener('scroll', updateScrollStatus)
  })
})
</script>

<template>
  <section
    class="relative py-5"
    :class="{
      'bg-white': template !== 'page' && template !== 'overlay' && data.theme !== 'light-grey',
      'bg-light-grey':
        template === 'page' || (template !== 'overlay' && data.theme === 'light-grey'),
    }"
  >
    <nav
      class="px-bleed absolute z-1 flex w-full justify-between"
      :class="[
        (data.medias?.length || 0) < 1 ? 'hidden' : '',
        (data.medias?.length || 0) < 3 ? 'lg:hidden' : '',
      ]"
    >
      <button
        aria-label="Slide to the previous media"
        class="bg-neon-yellow flex size-10 cursor-pointer items-center justify-center text-black transition-opacity lg:size-12"
        :class="!canScrollLeft ? 'pointer-events-none opacity-20' : 'opacity-100'"
        @click="previousSlide"
      >
        <IconsArrowE class="h-6 lg:h-8" />
      </button>
      <button
        aria-label="Slide to the next media"
        class="bg-neon-yellow flex size-10 cursor-pointer items-center justify-center text-black transition-opacity lg:size-12"
        :class="!canScrollRight ? 'pointer-events-none opacity-20' : 'opacity-100'"
        @click="nextSlide"
      >
        <IconsArrowW class="h-6 lg:h-8" />
      </button>
    </nav>

    <ul
      ref="$carousel"
      class="no-scrollbar gap-gap scroll-p-bleed flex snap-x snap-mandatory overflow-auto"
      :class="template === 'overlay' ? 'px-bleed' : ''"
    >
      <!-- weird math, align with the nav button -->
      <li
        v-for="(item, index) in data.medias"
        :key="index"
        :class="{
          'w-[calc(100%-var(--grid-gap)-3.25rem)] lg:w-[calc((100%-var(--grid-gap))/4-var(--grid-gap)-(0.75rem))]':
            template !== 'overlay',
          'w-[calc(100%-var(--grid-gap)-2.5rem)] lg:w-[calc((100%-var(--grid-gap))/3-var(--grid-gap)-0.57rem)]':
            template === 'overlay',
        }"
        class="shrink-0 snap-start"
      >
        <CommonMedia :sizes="['70vw', '20vw']" :media="item" />
      </li>
    </ul>
  </section>
</template>
